import { supabase } from '../lib/supabase.js';

export class TriggerDiagnostics {
  constructor() {
    this.isLoading = false;
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-6">Database Trigger Diagnostics</h2>
        
        <div class="space-y-6">
          <!-- Trigger Status Check -->
          <div class="bg-gray-50 p-4 rounded-md">
            <h3 class="font-medium text-gray-900 mb-4">Database Trigger Status</h3>
            <div id="trigger-status" class="text-sm">
              <div class="text-gray-500">Click "Check Trigger Status" to verify...</div>
            </div>
            <button 
              id="check-trigger-status" 
              class="mt-3 bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700"
            >
              Check Trigger Status
            </button>
          </div>

          <!-- Environment Variables Check -->
          <div class="bg-gray-50 p-4 rounded-md">
            <h3 class="font-medium text-gray-900 mb-4">Environment Variables</h3>
            <div id="env-status" class="text-sm">
              <div class="text-gray-500">Click "Check Environment" to verify...</div>
            </div>
            <button 
              id="check-env-status" 
              class="mt-3 bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700"
            >
              Check Environment
            </button>
          </div>

          <!-- Manual Trigger Test -->
          <div class="bg-gray-50 p-4 rounded-md">
            <h3 class="font-medium text-gray-900 mb-4">Manual Trigger Test</h3>
            <p class="text-sm text-gray-600 mb-4">
              This will manually update an order status to test if the trigger fires.
            </p>
            
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Select Order to Test
                </label>
                <select id="test-order-select" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">Loading orders...</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  New Status
                </label>
                <select id="test-status-select" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">Select new status...</option>
                  <option value="pending_pickup">Pending Pickup</option>
                  <option value="driver_dispatched_pickup">Driver Dispatched (Pickup)</option>
                  <option value="picked_up">Picked Up</option>
                  <option value="in_progress">In Progress</option>
                  <option value="ironing_complete">Ironing Complete</option>
                  <option value="ready_for_delivery">Ready for Delivery</option>
                  <option value="driver_dispatched_delivery">Driver Dispatched (Delivery)</option>
                  <option value="out_for_delivery">Out for Delivery</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              
              <button 
                id="test-trigger-btn" 
                class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                disabled
              >
                Test Trigger
              </button>
            </div>
          </div>

          <!-- Fix Trigger Configuration -->
          <div class="bg-yellow-50 p-4 rounded-md border border-yellow-200">
            <h3 class="font-medium text-yellow-900 mb-4">⚠️ Fix Trigger Configuration</h3>
            <p class="text-sm text-yellow-800 mb-4">
              If the trigger isn't working, click this button to recreate it with proper configuration.
            </p>
            <button 
              id="fix-trigger-btn" 
              class="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
            >
              Fix Database Trigger
            </button>
          </div>

          <!-- Results Display -->
          <div id="diagnostic-results" class="hidden">
            <h3 class="font-medium text-gray-900 mb-2">Diagnostic Results</h3>
            <div id="results-content" class="bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    await this.loadOrders();
    
    // Check trigger status
    document.getElementById('check-trigger-status').addEventListener('click', () => {
      this.checkTriggerStatus();
    });

    // Check environment variables
    document.getElementById('check-env-status').addEventListener('click', () => {
      this.checkEnvironmentVariables();
    });

    // Test trigger
    document.getElementById('test-trigger-btn').addEventListener('click', () => {
      this.testTrigger();
    });

    // Fix trigger
    document.getElementById('fix-trigger-btn').addEventListener('click', () => {
      this.fixTrigger();
    });

    // Order selection change
    document.getElementById('test-order-select').addEventListener('change', () => {
      this.updateTestButton();
    });

    // Status selection change
    document.getElementById('test-status-select').addEventListener('change', () => {
      this.updateTestButton();
    });
  }

  async loadOrders() {
    try {
      const { data: orders, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          status,
          customers (full_name)
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      const select = document.getElementById('test-order-select');
      select.innerHTML = '<option value="">Select an order...</option>';
      
      orders.forEach(order => {
        const option = document.createElement('option');
        option.value = order.id;
        option.textContent = `${order.order_number} - ${order.customers.full_name} (${order.status})`;
        select.appendChild(option);
      });

      this.orders = orders;
    } catch (error) {
      this.showResults('Error loading orders: ' + error.message, 'error');
    }
  }

  updateTestButton() {
    const orderSelect = document.getElementById('test-order-select');
    const statusSelect = document.getElementById('test-status-select');
    const testButton = document.getElementById('test-trigger-btn');
    
    testButton.disabled = !orderSelect.value || !statusSelect.value;
  }

  async checkTriggerStatus() {
    this.showResults('Checking database trigger status...', 'info');

    try {
      // Use the new comprehensive trigger check function
      const { data: triggerCheck, error: triggerError } = await supabase
        .rpc('check_trigger_functionality');

      if (triggerError) {
        this.showResults('Could not check trigger status: ' + triggerError.message, 'error');
        return;
      }

      const check = triggerCheck && triggerCheck.length > 0 ? triggerCheck[0] : null;
      
      if (!check) {
        this.showResults('No trigger status information returned', 'error');
        return;
      }
      
      document.getElementById('trigger-status').innerHTML = `
        <div class="space-y-2">
          <div class="flex items-center">
            <span class="w-3 h-3 rounded-full ${check.trigger_exists ? 'bg-green-500' : 'bg-red-500'} mr-2"></span>
            <span>Trigger exists: ${check.trigger_exists ? 'Yes' : 'No'}</span>
          </div>
          <div class="flex items-center">
            <span class="w-3 h-3 rounded-full ${check.function_exists ? 'bg-green-500' : 'bg-red-500'} mr-2"></span>
            <span>Function exists: ${check.function_exists ? 'Yes' : 'No'}</span>
          </div>
          ${check.trigger_exists && check.function_exists ? `
            <div class="text-xs text-gray-600">
              Trigger: order_status_change_trigger<br>
              Function: handle_order_status_change_trigger<br>
              Event: UPDATE OF status, payment_status<br>
              Timing: AFTER<br>
              Status: Fully configured
            </div>
          ` : ''}
        </div>
      `;

      const isFullyConfigured = check.trigger_exists && check.function_exists;
      this.showResults(
        isFullyConfigured 
          ? 'Trigger and function are properly configured' 
          : 'Trigger or function is missing/misconfigured!', 
        isFullyConfigured ? 'success' : 'error'
      );
    } catch (error) {
      this.showResults('Error checking trigger: ' + error.message, 'error');
    }
  }

  async checkEnvironmentVariables() {
    this.showResults('Checking environment variables...', 'info');

    const envStatus = document.getElementById('env-status');
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    envStatus.innerHTML = `
      <div class="space-y-2">
        <div class="flex items-center">
          <span class="w-3 h-3 rounded-full ${supabaseUrl ? 'bg-green-500' : 'bg-red-500'} mr-2"></span>
          <span>VITE_SUPABASE_URL: ${supabaseUrl ? 'Set' : 'Missing'}</span>
        </div>
        <div class="flex items-center">
          <span class="w-3 h-3 rounded-full ${supabaseAnonKey ? 'bg-green-500' : 'bg-red-500'} mr-2"></span>
          <span>VITE_SUPABASE_ANON_KEY: ${supabaseAnonKey ? 'Set' : 'Missing'}</span>
        </div>
        <div class="text-xs text-gray-600 mt-2">
          <strong>Note:</strong> The database trigger also needs SUPABASE_SERVICE_ROLE_KEY to be set in the database environment.
        </div>
      </div>
    `;

    this.showResults('Environment variables checked', 'info');
  }

  async testTrigger() {
    const orderId = document.getElementById('test-order-select').value;
    const newStatus = document.getElementById('test-status-select').value;

    if (!orderId || !newStatus) return;

    this.showResults(`Testing trigger by updating order to status: ${newStatus}...`, 'info');

    try {
      // Get current order status
      const { data: currentOrder, error: fetchError } = await supabase
        .from('orders')
        .select('status, order_number')
        .eq('id', orderId)
        .single();

      if (fetchError) throw fetchError;

      this.showResults(`Current status: ${currentOrder.status} → New status: ${newStatus}`, 'info');

      // Update the order status
      const { data: updatedOrder, error: updateError } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId)
        .select()
        .single();

      if (updateError) throw updateError;

      this.showResults(`Order ${currentOrder.order_number} status updated successfully!`, 'success');
      
      // Wait a moment then check for communication history
      setTimeout(async () => {
        await this.checkCommunicationHistory(orderId);
      }, 2000);

    } catch (error) {
      this.showResults('Error testing trigger: ' + error.message, 'error');
    }
  }

  async checkCommunicationHistory(orderId) {
    try {
      const { data: history, error } = await supabase
        .from('communication_history')
        .select('*')
        .eq('order_id', orderId)
        .order('sent_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      if (history.length > 0) {
        const latestComm = history[0];
        const timeDiff = new Date() - new Date(latestComm.sent_at);
        
        if (timeDiff < 10000) { // Within last 10 seconds
          this.showResults(`✅ SUCCESS: Communication triggered! Latest: ${latestComm.type} - ${latestComm.status}`, 'success');
        } else {
          this.showResults(`⚠️ Found communications but none recent. Latest was ${Math.round(timeDiff/1000)}s ago`, 'warning');
        }
      } else {
        this.showResults('❌ No communications found for this order. Trigger may not be working.', 'error');
      }
    } catch (error) {
      this.showResults('Error checking communication history: ' + error.message, 'error');
    }
  }

  async fixTrigger() {
    this.showResults('Fixing database trigger configuration...', 'info');

    try {
      // First, try to drop existing trigger
      await supabase.rpc('fix_order_trigger');
      
      this.showResults('Database trigger has been recreated successfully!', 'success');
      
      // Recheck trigger status
      setTimeout(() => {
        this.checkTriggerStatus();
      }, 1000);

    } catch (error) {
      this.showResults('Error fixing trigger: ' + error.message + '. You may need to run the migration manually.', 'error');
    }
  }

  showResults(message, type = 'info') {
    const resultsDiv = document.getElementById('diagnostic-results');
    const contentDiv = document.getElementById('results-content');
    
    resultsDiv.classList.remove('hidden');
    
    const timestamp = new Date().toLocaleTimeString();
    const colorClass = {
      'info': 'text-violet-600',
      'success': 'text-green-600', 
      'warning': 'text-yellow-600',
      'error': 'text-red-600'
    }[type] || 'text-gray-600';
    
    const resultHtml = `
      <div class="mb-4 p-3 border-l-4 border-${type === 'error' ? 'red' : type === 'success' ? 'green' : type === 'warning' ? 'yellow' : 'violet'}-400 bg-${type === 'error' ? 'red' : type === 'success' ? 'green' : type === 'warning' ? 'yellow' : 'violet'}-50">
        <div class="flex justify-between items-start">
          <pre class="${colorClass} text-sm whitespace-pre-wrap">${message}</pre>
          <span class="text-xs text-gray-500">${timestamp}</span>
        </div>
      </div>
    `;
    
    contentDiv.insertAdjacentHTML('afterbegin', resultHtml);
  }
}